import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { LIST_ITEMS_SIZE } from 'config';
import { profileSelectors } from 'store/profile';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import TabPanel from 'components/TabPanel';
import ListItem from 'components/CustomCollection/ListItem';
import EmptyDataStub from 'components/EmptyDataStub';
import LoginStub from 'components/LoginStub';
import WatchListIcon from 'components/Icons/menu-volia/watchlist';
import { StyledSectionTitle, StyledTabGrid } from './StyledComponents';
import { useSelector } from 'react-redux';

const TABS_SECTION_NAME = 'TABS_PANEL';
const TABS_ID = 'tabs_panel';
const ROW_SIZE = 3;

const TabContent = (props) => {
  const isLoggedIn = useSelector(profileSelectors.selectIsLoggedIn);
  const { title, tabs, isLoading } = props;
  const { onTabEnter, onContentEnter, loadExtraItems } = props;

  const onFocus = useCallback(
    (item, index, tab) => {
      const rows = tab.content?.data?.length / ROW_SIZE;
      const currentRowIndex = Math.ceil(index / ROW_SIZE);
      const isRequiresExtraLoad = currentRowIndex >= rows - 1 - 1;
      if (isRequiresExtraLoad && loadExtraItems) loadExtraItems(tab);
    },
    [loadExtraItems],
  );

  // set focus to tab/sub-tab if there is no content
  useEffect(() => {
    if (isLoading === false && isEmpty(tabs)) {
      SpatialNavigationWrapper.focus(`@${TABS_SECTION_NAME}`);
      SpatialNavigationWrapper.focus(`#${TABS_ID} .active`);
    }
  }, [isLoading, tabs]);

  return (
    <TabPanel
      sectionName={TABS_SECTION_NAME}
      id={TABS_ID}
      title={<StyledSectionTitle>{title}</StyledSectionTitle>}
      onEnter={onTabEnter}
      defaultTabId={tabs[0].id}
      startScrollIndex={4}
      viewSize={6}
      small
    >
      {tabs.map((tab) => {
        const isLoaded = tab.content?.isLoading === false;
        const showLoginStub = tab.loginRequired && !isLoggedIn;
        const showNoContent = isLoaded && isEmpty(tab.content?.data);

        let stub = null;

        if (showLoginStub) {
          stub = <LoginStub />;
        } else if (showNoContent) {
          stub = (
            <EmptyDataStub
              emptyPlaceholder="Content not found!"
              icon={<WatchListIcon width={192} height={192} />}
            />
          );
        }

        return (
          <TabPanel.Tab key={tab.id} id={tab.id} title={tab.name} path={`${tab.id}`}>
            {stub}
            {!stub && isLoaded && (
              <StyledTabGrid
                items={tab.content?.data || []}
                rowSize={ROW_SIZE}
                gridId={`grid-section-${tab.id}`}
                key={`grid-section-${tab.id}`}
                onEnter={onContentEnter}
                onFocus={(item, index) => onFocus(item, index, tab)}
                component={ListItem.VOD_ASSET}
                itemSize={LIST_ITEMS_SIZE.LARGE}
              />
            )}
          </TabPanel.Tab>
        );
      })}
    </TabPanel>
  );
};

TabContent.propTypes = {
  title: PropTypes.string.isRequired,
  tabs: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onTabEnter: PropTypes.func.isRequired,
  onContentEnter: PropTypes.func.isRequired,
};

export default TabContent;
